// Shared Constants: Status Enums
// This file is the single source of truth for all status values.
// It is copied to frontend/src/shared/ and backend/src/shared/ by sync-shared.js

// Fatura (Invoice) Status
export const INVOICE_STATUS = {
    KESILECEK: 'KESILECEK',
    KESILDI: 'KESILDI',
    IPTAL: 'IPTAL',
    IADE_EDILDI: 'IADE_EDILDI',
    KISMI_IADE: 'KISMI_IADE',  // [YENİ] Kısmi iade edilmiş parent — ciroda kalır
    GEREKMIYOR: 'GEREKMIYOR'
} as const;

// Teslimat (Delivery) Status
export const DELIVERY_STATUS = {
    EVET: 'EVET',
    HAYIR: 'HAYIR',
    KISMI: 'KISMI'
} as const;

// Sipariş Türü (Order Type)
export const ORDER_TYPE = {
    SATIS: 'SATIS',
    YEDEK_PARCA: 'YEDEK_PARCA',
    IADE: 'IADE',
    TELAFI: 'TELAFI',
    IHRACAT: 'IHRACAT',
    TEKLIF: 'TEKLIF', // [YENİ] Teklif Modülü İçin Eklendi
    STOK_IMALAT: 'STOK_IMALAT' // [YENİ] Stok için imalat — ciro/sayım dışı, sadece üretim ekranı
} as const;

// [İADE/TELAFİ] İade Durumu
export const IADE_DURUMU = {
    BEKLIYOR: 'BEKLIYOR',
    TESLIM_ALINDI: 'TESLIM_ALINDI',
    TAMAMLANDI: 'TAMAMLANDI'
} as const;

// [TEKLIF] Teklif Durumu - [YENİ]
export const TEKLIF_DURUMU = {
    BEKLEMEDE: 'BEKLEMEDE',
    ONAYLANDI: 'ONAYLANDI',
    IPTAL: 'IPTAL',
    SURESI_DOLDU: 'SURESI_DOLDU',
    OLUMSUZ: 'OLUMSUZ'      // [OLUMSUZ] Müşteri olumsuz kararı
} as const;

// Ödeme Türü (Payment Type) — siparisler.odeme_turu → odeme_tipi ENUM
export const PAYMENT_TYPE = {
    NAKIT: 'Nakit',
    KREDI_KARTI: 'Kredi Karti',
    HAVALE: 'Havale',
    BEDELSIZ: 'Bedelsiz'
} as const;

// Ödeme Yöntemi — odeme_kayitlari.odeme_yontemi → odeme_yontemi_enum
//
// ⚠️ PAYMENT_TYPE ile KARIŞTIRILMAMALI: ikisi ayrı kolon, ayrı ENUM, ayrı yazım.
// PAYMENT_TYPE siparişin ödeme türüdür ve değerleri Türkçe okunur ('Kredi Karti');
// bu ise tek tek ödeme KAYITLARININ yöntemidir ve BÜYÜK_HARF yazılır ('KREDI_KARTI').
// `shopifyService.mapLatestPaymentGateway` bu ayrımı zaten yorumunda uyarıyordu.
//
// DIGER: yöntemi bilinmeyen kayıt. Shopify/Trendyol iade ve otomatik ödeme
// kayıtları gateway çözülemediğinde bunu yazar.
export const ODEME_YONTEMI = {
    NAKIT: 'NAKIT',
    HAVALE: 'HAVALE',
    KREDI_KARTI: 'KREDI_KARTI',
    DIGER: 'DIGER'
} as const;

// Ürün Tipi (Product Type)
export const PRODUCT_TYPE = {
    STANDART: 'standart',
    BUNDLE: 'bundle',
    YEDEK_PARCA: 'yedek_parca',
    SERVIS: 'servis'
} as const;

// [YENİ — KISMI_IADE] Ciro Hesaplama Filtreleri
// SUM ve COUNT için AYRI filtre setleri vardır (IADE child'lar ciro SUM'ında negatif,
// sipariş COUNT'unda hariç).
//
// Ciro hesaplama mantığı:
//   - Parent IADE_EDILDI (tam iade) → exclude (ciroda yer almaz)
//   - Parent KISMI_IADE → include (ciroda tam tutarı ile kalır)
//   - IADE child'lar → SUM'a NEGATIF katılır (iade tarihinde cirodan düşer)
//   - IADE child'ın iç pazar / ihracat ayrımı: kaynak_siparis_turu kolonuyla
//
// Örnek: Parent SATIS 100.000 TL + IADE child 30.000 TL (kısmi)
//   → Parent KISMI_IADE, SUM'a +100.000 girer
//   → IADE child'a SUM'a -30.000 girer
//   → Net ciro katkısı: 70.000 TL ✓
export const REVENUE_FILTERS = {
    // ====== SUM (ciro tutar) filtreleri ======
    // IADE_EDILDI parent + IADE child BİRLİKTE dahil — CASE WHEN formülüyle birbirini dengeler:
    //   Tam iade: parent +1565 + child -1565 = 0 ✓
    //   Kısmi iade: parent +34816 + child -28568 = 6247 ✓
    // Eskiden IADE_EDILDI exclude ediliyordu → bu tam iadede -child bırakıyordu (BUG).
    EXCLUDED_INVOICE_STATUSES_SUM: ['IPTAL'],
    EXCLUDED_ORDER_TYPES_SUM: ['TEKLIF', 'STOK_IMALAT'],

    // ====== COUNT (sipariş sayı) filtreleri ======
    // IADE_EDILDI parent + IADE child ikisi de sayı sayımında yer almaz
    // (tam iade edilmiş sipariş "aktif satış sayısı"na dahil değildir)
    EXCLUDED_INVOICE_STATUSES_COUNT: ['IPTAL', 'IADE_EDILDI'],
    EXCLUDED_ORDER_TYPES_COUNT: ['TEKLIF', 'IADE', 'STOK_IMALAT'],

    // ====== Backward compatibility (eski kod için) ======
    // @deprecated Yeni kodlar SUM/COUNT ayrımını kullansın
    EXCLUDED_ORDER_TYPES: ['TEKLIF', 'IHRACAT', 'IADE', 'STOK_IMALAT'],
    EXCLUDED_INVOICE_STATUSES: ['IPTAL', 'IADE_EDILDI'],

    // ====== SQL snippet'leri (backend için kolay kullanım) ======

    // Net ciro SUM ifadesi — IADE child'lar negatif katılır
    // Kullanım: `SELECT ${REVENUE_FILTERS.SQL_REVENUE_SUM_EXPR} FROM siparisler WHERE ${REVENUE_FILTERS.SQL_DOMESTIC_REVENUE_SUM}`
    SQL_REVENUE_SUM_EXPR: "SUM(CASE WHEN siparis_turu = 'IADE' THEN -toplam_tutar ELSE toplam_tutar END)",

    // İç pazar ciro (SATIS + SATIS parent'lı IADE child'lar)
    // IADE_EDILDI parent'lar DAHİL — IADE child negatif sum ile sıfırlar (tam iade net=0).
    // COALESCE: kaynak_siparis_turu NULL ise (deploy window edge case) default 'SATIS' → iç pazar
    SQL_DOMESTIC_REVENUE_SUM: "aktif_mi = true AND fatura_durumu NOT IN ('IPTAL') AND (siparis_turu IN ('SATIS', 'YEDEK_PARCA', 'TELAFI') OR (siparis_turu = 'IADE' AND COALESCE(kaynak_siparis_turu, 'SATIS') IN ('SATIS', 'YEDEK_PARCA', 'TELAFI')))",

    // İhracat ciro (IHRACAT + IHRACAT parent'lı IADE child'lar)
    SQL_EXPORT_REVENUE_SUM: "aktif_mi = true AND fatura_durumu NOT IN ('IPTAL') AND (siparis_turu = 'IHRACAT' OR (siparis_turu = 'IADE' AND kaynak_siparis_turu = 'IHRACAT'))",

    // Toplam ciro (iç pazar + ihracat birlikte)
    SQL_TOTAL_REVENUE_SUM: "aktif_mi = true AND fatura_durumu NOT IN ('IPTAL') AND siparis_turu NOT IN ('TEKLIF', 'STOK_IMALAT')",

    // Sipariş SAYI filtresi — IADE_EDILDI parent + IADE child hariç
    SQL_DOMESTIC_COUNT: "aktif_mi = true AND fatura_durumu NOT IN ('IPTAL', 'IADE_EDILDI') AND siparis_turu NOT IN ('TEKLIF', 'IHRACAT', 'IADE', 'STOK_IMALAT')",
    SQL_EXPORT_COUNT: "aktif_mi = true AND fatura_durumu NOT IN ('IPTAL', 'IADE_EDILDI') AND siparis_turu = 'IHRACAT'",
    SQL_TOTAL_COUNT: "aktif_mi = true AND fatura_durumu NOT IN ('IPTAL', 'IADE_EDILDI') AND siparis_turu NOT IN ('TEKLIF', 'IADE', 'STOK_IMALAT')",

    // ====== Deprecated (eski isimler, compatibility için) ======
    // @deprecated Yeni kodlar SQL_DOMESTIC_REVENUE_SUM + SQL_DOMESTIC_COUNT ayrımını kullansın
    SQL_DOMESTIC_REVENUE: "aktif_mi = true AND fatura_durumu NOT IN ('IPTAL', 'IADE_EDILDI') AND siparis_turu NOT IN ('TEKLIF', 'IHRACAT', 'IADE', 'STOK_IMALAT')",
    SQL_EXPORT_REVENUE: "aktif_mi = true AND fatura_durumu NOT IN ('IPTAL', 'IADE_EDILDI') AND siparis_turu = 'IHRACAT'"
} as const;

// Derived arrays for validation
export const ALL_INVOICE_STATUSES = Object.values(INVOICE_STATUS);
export const ALL_DELIVERY_STATUSES = Object.values(DELIVERY_STATUS);
export const ALL_ORDER_TYPES = Object.values(ORDER_TYPE);
export const ALL_PAYMENT_TYPES = Object.values(PAYMENT_TYPE);
export const ALL_ODEME_YONTEMI = Object.values(ODEME_YONTEMI);
export const ALL_PRODUCT_TYPES = Object.values(PRODUCT_TYPE);
export const ALL_IADE_DURUMU = Object.values(IADE_DURUMU);
export const ALL_TEKLIF_DURUMU = Object.values(TEKLIF_DURUMU); // [YENİ]

// Teklif listesi filtresindeki SANAL değerler — `teklif_durumu` ENUM'unda YOK ve
// kolona asla yazılmazlar. `models/order.js` bunları sorguya çevirir:
//   OLUMLU        = aktif onaylı teklifler + siparişe dönüşmüş olanlar
//   DONUSTURULDU  = yalnız siparişe dönüşmüş olanlar
// Rota 3.29'a kadar iki yerde düz metindi (arayüzün filtre listesi ve model).
// Sorgu doğrulaması üçüncü tüketici olunca tek kaynağa taşındı — üçü ayrışırsa
// arayüzde seçilebilen bir değer sunucuda reddedilir.
export const TEKLIF_SANAL_FILTRELERI = {
    OLUMLU: 'OLUMLU',
    DONUSTURULDU: 'DONUSTURULDU'
} as const;

export const ALL_TEKLIF_SANAL_FILTRE = Object.values(TEKLIF_SANAL_FILTRELERI);

// Teklif durumu filtresinde kabul edilen değerlerin TAMAMI: gerçek ENUM değerleri
// artı iki sanal değer. Sorgu şeması bunu kullanır.
export const ALL_TEKLIF_FILTRE_DEGERI = [...ALL_TEKLIF_DURUMU, ...ALL_TEKLIF_SANAL_FILTRE];

// Siparişin ödeme durumu — DB kolonu değil, sunucu her okumada `SQL_ODEME_DURUMU` ile
// hesaplar. Sipariş listesinin rozeti, pano penceresinin rozeti ve listenin ödeme
// süzgeci aynı ifadeyi okur; süzgeç rozetin göstermediği satırı seçemez.
export const ODEME_DURUMU = {
    TEKLIF: 'TEKLIF',
    IPTAL: 'IPTAL',
    IADE: 'IADE',
    BEDELSIZ: 'BEDELSIZ',
    ODENDI: 'ODENDI',
    KISMI: 'KISMI',
    ODENMEDI: 'ODENMEDI'
} as const;

export const ALL_ODEME_DURUMU = Object.values(ODEME_DURUMU);

// İlk tutan dal kazanır, sıra önemli. İade kaydı ve tam iade edilmiş sipariş ödeme
// durumu göstermez: iade kaydı para taşımaz, tam iadede para ana siparişe eksi kayıtla
// yazılır (business_rules §2.3); kısmi iade edilmiş sipariş ödeme durumunu gösterir.
// Bedelsiz ödeme türünden ya da sıfır tutardan anlaşılır: sunucu stok imalatında toplamı her
// kayıtta, garantiye alırken bir kez sıfırlar (garantili siparişe sonradan tutar girilebilir,
// o zaman ödeme durumu görünür); stok imalatında ödeme türü boş kalabilir. Kolonlar tablo
// öneki olmadan yazılır: ifade `siparisler`, `v_siparisler_ciro` ve müşteri birleşimli liste
// sorgusunda çalışır.
export const SQL_ODEME_DURUMU = "(CASE WHEN siparis_turu = 'TEKLIF' THEN 'TEKLIF' WHEN fatura_durumu = 'IPTAL' THEN 'IPTAL' WHEN siparis_turu = 'IADE' OR fatura_durumu = 'IADE_EDILDI' THEN 'IADE' WHEN odeme_turu = 'Bedelsiz' OR toplam_tutar = 0 THEN 'BEDELSIZ' WHEN odenen_tutar >= toplam_tutar THEN 'ODENDI' WHEN odenen_tutar > 0 THEN 'KISMI' ELSE 'ODENMEDI' END)";

// Entegrasyon log satırının işlem durumu — `entegrasyon_loglari.islem_durumu`.
// Kolon serbest metin, DB doğrulamaz; değeri backend yazar, ekran rengini ondan seçer.
// Gelen bildirimin satırı işlenmeden önce `alindi` ile açılır ve sonuçla kapanır;
// `alindi`da kalan satır yarım kalmış denemedir. `eslesmedi`: siparişi bulunamayan bildirim.
export const ENTEGRASYON_LOG_DURUMU = {
    ALINDI: 'alindi',
    ISLENDI: 'islendi',
    ATILDI: 'atildi',
    ESLESMEDI: 'eslesmedi',
    FARK_VAR: 'fark_var',
    DUZELTILDI: 'duzeltildi',
    HATA: 'hata'
} as const;

export const ALL_ENTEGRASYON_LOG_DURUMU = Object.values(ENTEGRASYON_LOG_DURUMU);

// Log satırının sonuç nedeni — `entegrasyon_loglari.atilma_nedeni` (kolon adı eski; atılma
// dışında da sonucun nedenini taşır, ör. `teslim_korundu` işlenmiş satırdadır). Değeri
// backend yazar; ekran ham gösterir.
export const ENTEGRASYON_LOG_NEDENI = {
    MAGAZA_PASIF: 'magaza_pasif',
    BILINMEYEN_MAGAZA: 'bilinmeyen_magaza',
    TEKRAR: 'tekrar',
    ESKI_BILDIRIM: 'eski_bildirim',
    ISLENMEYEN_KONU: 'islenmeyen_konu',
    MUKERRER: 'mukerrer',
    EKSIK_ALAN: 'eksik_alan',
    TESLIM_KORUNDU: 'teslim_korundu'
} as const;

// Hepsiburada sipariş kontrolü (farkları bulur, dördünü kendisi kapatır). Değerleri backend
// kontrol servisi üretir, entegrasyon ekranı etiketler. Log satırının payload'ında durur.
export const HB_KONTROL_FARK_TURU = {
    CRMDE_YOK: 'CRMDE_YOK',
    KALEM_EKSIK: 'KALEM_EKSIK',
    SILINMIS: 'SILINMIS',
    BASKA_MAGAZADA: 'BASKA_MAGAZADA',
    PAKET_BAGI_YOK: 'PAKET_BAGI_YOK',
    TESLIM_FARKI: 'TESLIM_FARKI',
    IPTAL_FARKI: 'IPTAL_FARKI',
    IPTAL_TERS: 'IPTAL_TERS',
    TANINMAYAN_DURUM: 'TANINMAYAN_DURUM',
    CIFT_KAYIT: 'CIFT_KAYIT',
    OKUNAMADI: 'OKUNAMADI',
    BICIM_TANINMADI: 'BICIM_TANINMADI',
    SAYFA_SINIRI: 'SAYFA_SINIRI'
} as const;

export const ALL_HB_KONTROL_FARK_TURU = Object.values(HB_KONTROL_FARK_TURU);

// Kontrolün sipariş numarası topladığı Hepsiburada listeleri.
export const HB_KONTROL_LISTESI = {
    BEKLEYEN_KALEMLER: 'BEKLEYEN_KALEMLER',
    PAKETLER: 'PAKETLER',
    KARGOYA_VERILENLER: 'KARGOYA_VERILENLER',
    TESLIM_EDILENLER: 'TESLIM_EDILENLER'
} as const;

export const ALL_HB_KONTROL_LISTESI = Object.values(HB_KONTROL_LISTESI);

// Kontrolün bir mağazayı neden denetlemediği (log satırı açılmaz, yalnız cevapta).
export const HB_KONTROL_ATLAMA_NEDENI = {
    KIMLIK_YOK: 'KIMLIK_YOK',
    PASIF: 'PASIF'
} as const;

export const ALL_HB_KONTROL_ATLAMA_NEDENI = Object.values(HB_KONTROL_ATLAMA_NEDENI);

// Kontrolün kapattığı fark: açılan sipariş ya da yazılan paket, teslim, iptal.
// Log satırının payload'ında ve sabah raporunun özet satırında durur.
export const HB_KONTROL_DUZELTME = {
    ACILDI: 'ACILDI',
    PAKET: 'PAKET',
    TESLIM: 'TESLIM',
    IPTAL: 'IPTAL'
} as const;

export const ALL_HB_KONTROL_DUZELTME = Object.values(HB_KONTROL_DUZELTME);

// Kontrolün bir farkı neden kapatmadığı; raporda kalan farkın yanında durur. Detay kalemi
// uyarlayıcısının ret kodları (EKSIK_ALAN, INDIRIMLI) da bu kümeden.
export const HB_KONTROL_DUZELTILMEME_NEDENI = {
    DURUM: 'DURUM',
    TALEP_ACIK: 'TALEP_ACIK',
    EKSIK_ALAN: 'EKSIK_ALAN',
    YENI_SIPARIS: 'YENI_SIPARIS',
    ODEME_BEKLIYOR: 'ODEME_BEKLIYOR',
    INDIRIMLI: 'INDIRIMLI',
    SONRADAN_KALEM: 'SONRADAN_KALEM',
    ELLE_GIRILMIS_OLABILIR: 'ELLE_GIRILMIS_OLABILIR',
    ELLE_GIRILMIS: 'ELLE_GIRILMIS',
    TESLIM_EDILEMEDI: 'TESLIM_EDILEMEDI',
    FATURA_DURUMU: 'FATURA_DURUMU',
    HATA: 'HATA'
} as const;

export const ALL_HB_KONTROL_DUZELTILMEME_NEDENI = Object.values(HB_KONTROL_DUZELTILMEME_NEDENI);

// Type exports for TypeScript consumers
export type InvoiceStatusType = typeof ALL_INVOICE_STATUSES[number];
export type DeliveryStatusType = typeof ALL_DELIVERY_STATUSES[number];
export type OrderType = typeof ALL_ORDER_TYPES[number];
export type PaymentType = typeof ALL_PAYMENT_TYPES[number];
export type OdemeYontemiType = typeof ALL_ODEME_YONTEMI[number];
export type ProductType = typeof ALL_PRODUCT_TYPES[number];
export type IadeDurumuType = typeof ALL_IADE_DURUMU[number];
export type TeklifDurumuType = typeof ALL_TEKLIF_DURUMU[number]; // [YENİ]
export type OdemeDurumuType = typeof ALL_ODEME_DURUMU[number];
export type EntegrasyonLogDurumuType = typeof ALL_ENTEGRASYON_LOG_DURUMU[number];
export type HbKontrolFarkTuruType = typeof ALL_HB_KONTROL_FARK_TURU[number];
export type HbKontrolListesiType = typeof ALL_HB_KONTROL_LISTESI[number];
export type HbKontrolAtlamaNedeniType = typeof ALL_HB_KONTROL_ATLAMA_NEDENI[number];
export type HbKontrolDuzeltmeType = typeof ALL_HB_KONTROL_DUZELTME[number];
export type HbKontrolDuzeltilmemeNedeniType = typeof ALL_HB_KONTROL_DUZELTILMEME_NEDENI[number];
